import { useEffect, useMemo, useState } from 'react'

import { useAddressContext } from '@/context/AddressContext'
import { useAuthContext } from '@/context/AuthContext'
import { useOperationalModelContext } from '@/context/OperationalModelContext'
import { useStoreContext } from '@/context/StoreContext'
import { useCartContext } from '@mercadoni/sinister/dist/cjs/cart'
import { storage } from '@mercadoni/sinister/dist/cjs/shared/utils'

import { useHandPicking } from '@mercadoni/sinister/dist/cjs/shared/contexts'
import useCartStatusWatcher from './useCartStatusWatcher'
import formatOperationalModelCart from './useCase/formatOperationalModelCart'
import initializeCart from './useCase/initializeCart'

import SentryTags from '@/components/SentryTags'
import { CartModel } from '@/useCases/cart/types'
import { ClientModel } from '@mercadoni/sinister/dist/lib/src/entities/clientModel'
import { DeadpoolUser, ProductCatalog } from '~types/index'
import { CART_ID } from './constants'
import shouldAddAddressIdToCart from './useCase/shouldAddAddressIdToCart'
import updateIsMigrableCart, {
  UpdateIsMigrableCartProps,
} from './useCase/updateIsMigrableCart'

interface AuthContextResponse {
  customer: DeadpoolUser | null
  isInitialized: boolean
}

const MiddleWare = ({
  clientModel,
  children,
  onAddProductHandPicking,
}: {
  children: React.ReactNode
  clientModel: ClientModel
  onAddProductHandPicking: (product: ProductCatalog) => void
}) => {
  const db = storage('local')
  const cartIdStored = db.getItem(CART_ID) || null
  const [isLoading, setIsLoading] = useState(false)
  const { customer, isInitialized }: AuthContextResponse = useAuthContext()
  const { store } = useStoreContext()
  const { operationalModel } = useOperationalModelContext()
  const { address } = useAddressContext()
  const {
    cart,
    addProduct,
    getProduct,
    type,
    initializer,
    restart,
    cartId,
    products,
    refetch,
  } = useCartContext()

  useHandPicking({ cart, addProduct, getProduct, onAddProductHandPicking })
  useCartStatusWatcher()

  const shouldInitialize = useMemo(
    () => isInitialized && !!store && !!operationalModel.opsModel,
    [isInitialized, store, operationalModel]
  )

  const baseParams = useMemo(
    () => ({
      clientId: clientModel.clientId as string,
      storeReference: store?.reference || store?.code, // TODO: point of failure, cart might have other store reference
      addressId: address?.id,
      operationalModel:
        operationalModel?.opsModel &&
        formatOperationalModelCart(operationalModel.opsModel),
    }),
    [clientModel, store, address, operationalModel]
  )

  const updateLoggedCart = async (cart: CartModel) => {
    const response = await restart({
      ...baseParams,
      storeReference: cart?.store?.reference || baseParams.storeReference,
      operationalModel: cart?.operationalModel || baseParams.operationalModel,
    })
    console.log(
      '🚀 CartContext/MiddleWare  - updateLoggedCartWithAddress',
      response
    )
    return response
  }

  const startInitiliazeCart = async () => {
    await initializeCart({
      baseParams,
      customer,
      cartId,
      initializer,
      restart,
    })
  }

  useEffect(() => {
    const params: UpdateIsMigrableCartProps = {
      shouldInitialize,
      customer,
      products,
      type,
      cartId,
    }

    updateIsMigrableCart(params)
  }, [products, cartId, type, customer, shouldInitialize])

  useEffect(() => {
    if (shouldInitialize && !cartIdStored && !isLoading) {
      console.log('🛒 ~ MiddleWare ~ shouldInitialize:', shouldInitialize)
      // 1. Do not initialize cart if no address is selected when logged and operational model is delivery
      const isNoAddressSelected =
        operationalModel.opsModel === 'DELIVERY' && customer?.id && !address?.id
      if (isNoAddressSelected) {
        console.log(
          '🛒 ~ MiddleWare ~ but no Address selected, skipping initialization'
        )
        return
      }

      startInitiliazeCart()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldInitialize,
    store,
    operationalModel,
    customer,
    address,
    cartIdStored,
    isLoading,
  ])

  const initializeCartByCartId = async () => {
    setIsLoading(true)
    await initializer({
      customer: customer?.id,
      isLogged: !!customer,
      loggedCartId: cartIdStored,
      callback: (type: string) => {
        console.log('🚀 initializeCartByCartId  - callback', type)
        setIsLoading(false)
      },
    })
  }

  useEffect(() => {
    const shouldInitializeByCartId = isInitialized && cartIdStored && !cart?.id
    if (shouldInitializeByCartId) {
      console.log(
        '🚀 ~ MiddleWare ~ shouldInitializeByCartId:',
        shouldInitializeByCartId
      )
      initializeCartByCartId()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, cartIdStored, cart])

  useEffect(() => {
    if (!cart?.id) {
      return
    }
    if (shouldAddAddressIdToCart({ cart, address, operationalModel })) {
      console.log('🛒 ~ MiddleWare ~ shouldAddAddressIdToCart')
      updateLoggedCart(cart)
    }
    if (cart?.status === 'DELETED') {
      console.log('🛒 ~ MiddleWare ~ cart status is DELETED')
      refetch({ shouldValidate: false, shouldCreate: true })
      return
    }
    if (cart?.id) {
      console.log('🛒  ~ MiddleWare ~ cart:', cart)
      SentryTags({
        clientId: clientModel.clientId as string,
        storeReference: cart?.store?.reference || cart?.store?.code,
        operationalModel: cart?.operationalModel,
      })
      return
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart])

  return children
}

export default MiddleWare
